import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { getDecodedUserId } from "../../Services/GeneralService";
import "react-quill/dist/quill.snow.css";

// Services & Icons
import {
  getBlogById,
  deleteBlogById,
  incrementBlogView,
} from "../../Services/BlogService";
import {
  createComment,
  getCommentForBlog,
  deleteComment,
  replyComment,
  checkComment,
} from "../../Services/CommentService";
import { CheckRole, isLoggedIn } from "../../Services/AuthService";
import {
  FaArrowRight,
  FaRegClock,
  FaUser,
  FaCalendarAlt,
  FaEye,
  FaEdit,
  FaTrash,
  FaEllipsisV,
  FaReply,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import AddCommentImg from "../../assets/AddComment.png";

// Components
import Loader from "../../Components/Core/Loader";
import InfoFooter from "../../Components/Core/InfoFooter";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import Notification from "../../Components/Core/Notification";
import SeoHead from "../../Components/Core/SeoHead";
import { useTenantContent } from "../../Context/TenantContentContext";
import {
  buildBlogPostingJsonLd,
  joinUrl,
  blogDetailPath,
} from "../../utils/seo";
import "./Blog-detail.css";
import { prepareArticle } from "../../utils/articleStructure";

// --- Utility Functions ---
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = (now - date) / 1000; // seconds

  if (diff < 60) return "چند ثانیه قبل";
  if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه قبل`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت قبل`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} روز قبل`;
  if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))} هفته قبل`;
  if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))} ماه قبل`;
  return `${Math.floor(diff / (86400 * 365))} سال قبل`;
};

// --- Custom Hook for Logic (FIXED) ---
const useBlogLogic = (slug, initialArticle) => {
  const navigate = useNavigate();
  const [blog, setBlog] = useState(initialArticle || null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(!initialArticle);
  const [userId, setUserId] = useState(0);
  const viewCountedRef = useRef(false);

  // UI State
  const [openDialog, setOpenDialog] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };
  useEffect(() => {
    setUserId(getDecodedUserId());

    const loadBlog = async () => {
      try {
        const decodedSlug = (() => {
          try {
            return decodeURIComponent(slug || "");
          } catch {
            return slug || "";
          }
        })();
        let data = initialArticle;
        if (!data || (data.slug && data.slug !== decodedSlug && String(data.id) !== decodedSlug)) {
          data = await getBlogById(decodedSlug);
        }
        setBlog(data);

        if (data?.id && !viewCountedRef.current) {
          viewCountedRef.current = true;
          const { viewCount } = await incrementBlogView(data.id);
          setBlog((prev) => (prev ? { ...prev, viewCount } : prev));
        }

        if (data?.slug && decodedSlug !== data.slug) {
          navigate(blogDetailPath(data), { replace: true });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  // Fetch Comments
  const fetchComments = useCallback(async () => {
    if (!blog?.id) return;
    try {
      const data = await getCommentForBlog(blog.id);
      setComments(data);
    } catch (error) {
      console.error(error);
    }
  }, [blog?.id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Actions
  const handleDeleteBlog = async () => {
    try {
      await deleteBlogById(blog.id);
      navigate("/blog/index");
    } catch (error) {
      showNotification("خطا در حذف مقاله", "error");
    }
  };

  const handleAddComment = async (text) => {
    try {
      const commentData = {
        BlogId: blog.id,
        Content: text,
      };
      await createComment(commentData);
      await fetchComments();
      showNotification("نظر شما با موفقیت ثبت شد و در صف انتشار قرار گرفت");
      return true;
    } catch (error) {
      showNotification(error.message, "error");
      return false;
    }
  };

  const handleReplyComment = async (text, commentId) => {
    try {
      const replyData = {
        BlogId: blog.id,
        Content: text,
      };
      const data = await replyComment(replyData, commentId);
      if (data.commentId) {
        await checkComment(data.commentId);
        await fetchComments();
      }

      showNotification("پاسخ شما با موفقیت ثبت شد");
      return true;
    } catch (error) {
      showNotification(error.message, "error");
      return false;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      await fetchComments();
      showNotification("دیدگاه با موفقیت حذف شد");
    } catch (error) {
      showNotification("خطا در حذف دیدگاه", "error");
    }
  };

  return {
    blog,
    comments,
    loading,
    userId,
    openDialog,
    setOpenDialog,
    notification,
    setNotification,
    handleDeleteBlog,
    handleAddComment,
    handleReplyComment,
    handleDeleteComment,
    navigate,
  };
};

// --- Sub-Component: Single Comment Item ---
const CommentItem = ({ comment, userId, onDelete, onReply, level = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState("");

  // Close menu when clicking outside
  useEffect(() => {
    const closeMenu = () => setIsMenuOpen(false);
    if (isMenuOpen) document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, [isMenuOpen]);

  const canDelete =
    isLoggedIn() &&
    (CheckRole(["Admin", "Doctor", "Secretary"]) || comment.userId === userId);

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;
    const success = await onReply(replyText, comment.id);
    if (success) {
      setReplyText("");
      setShowReplyForm(false);
      setShowReplies(true);
    }
  };

  const hasReplies = comment.replies && comment.replies.length > 0;

  return (
    <div
      className={`comment-item ${level > 0 ? "comment-reply" : ""}`}
      style={{ marginRight: level > 0 ? "40px" : "0" }}
    >
      {canDelete && (
        <div className="comment-menu-wrapper">
          <FaEllipsisV
            className="comment-menu-icon fs-5 mt-1"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
          />
          {isMenuOpen && (
            <div className="comment-dropdown">
              <button
                className="dropdown-item delete-item iran-sans"
                onClick={() => onDelete(comment.id)}
              >
                <FaTrash size={12} />
                <span>حذف دیدگاه</span>
              </button>
            </div>
          )}
        </div>
      )}

      <div className="comment-header-item">
        <div className="comment-avatar">
          <FaUser size={18} />
        </div>
        <div className="comment-meta">
          <span className="comment-author iran-sans">{comment.fullName}</span>
          <span className="comment-date iran-sans">
            {formatRelativeTime(comment.date)}
          </span>
        </div>
      </div>

      <p className="comment-text iran-sans">{comment.content}</p>

      <div className="comment-actions">
        {isLoggedIn() && CheckRole(["Admin", "Doctor"]) && (
          <button
            className="comment-action-btn iran-sans"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            <FaReply size={14} />
            <span>پاسخ</span>
          </button>
        )}

        {hasReplies && (
          <button
            className="comment-action-btn iran-sans"
            onClick={() => setShowReplies(!showReplies)}
          >
            {showReplies ? (
              <FaChevronUp size={14} />
            ) : (
              <FaChevronDown size={14} />
            )}
            <span>
              {showReplies ? "بستن پاسخ‌ها" : `${comment.replies.length} پاسخ`}
            </span>
          </button>
        )}
      </div>

      {showReplyForm && (
        <div className="reply-form">
          <textarea
            className="comment-textarea iran-sans"
            rows="3"
            placeholder="پاسخ خود را بنویسید..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
          <div className="reply-form-actions">
            <button
              className="submit-reply-btn iran-sans"
              disabled={!replyText.trim()}
              onClick={handleReplySubmit}
            >
              ارسال پاسخ
            </button>
            <button
              className="cancel-reply-btn iran-sans"
              onClick={() => {
                setShowReplyForm(false);
                setReplyText("");
              }}
            >
              لغو
            </button>
          </div>
        </div>
      )}

      {showReplies && hasReplies && (
        <div className="replies-container">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              userId={userId}
              onDelete={onDelete}
              onReply={onReply}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- Main Component ---
const BlogDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [commentText, setCommentText] = useState("");
  const [failedImage, setFailedImage] = useState(null);
  const [contentsOpen, setContentsOpen] = useState(false);
  const { content } = useTenantContent();

  const {
    blog,
    comments,
    loading,
    userId,
    openDialog,
    setOpenDialog,
    notification,
    setNotification,
    handleDeleteBlog,
    handleAddComment,
    handleReplyComment,
    handleDeleteComment,
    navigate,
  } = useBlogLogic(slug, location.state?.article);

  const onCommentSubmit = async () => {
    if (!commentText.trim()) return;
    const success = await handleAddComment(commentText);
    if (success) setCommentText("");
  };

  const article = useMemo(() => prepareArticle(blog?.description), [blog?.description]);

  const readingMinutes = useMemo(() => {
    const text = new DOMParser().parseFromString(article.html, "text/html").body.textContent;
    return Math.max(1, Math.ceil(text.trim().split(/\s+/).filter(Boolean).length / 200));
  }, [article.html]);

  if (loading) return <Loader />;

  // Count total comments including replies
  const countComments = (comments) => {
    return comments.reduce((total, comment) => {
      return total + 1 + (comment.replies ? countComments(comment.replies) : 0);
    }, 0);
  };

  const totalComments = countComments(comments);
  const pageUrl = joinUrl(
    content?.canonicalUrl,
    blogDetailPath(blog || { slug }).replace(/^\//, ""),
  );
  const pageTitle = blog?.title
    ? `${blog.title} | ${content?.siteName || ""}`
    : content?.siteName;
  const pageDescription = blog?.abstract || content?.siteDescription;

  return (
    <>
      {blog && content && (
        <SeoHead
          title={pageTitle}
          description={pageDescription}
          keywords={content.siteKeywords}
          canonical={pageUrl}
          image={blog.imageUrl || content.ogImage}
          imageAlt={blog.title}
          type="article"
          siteName={content.siteName}
          publishedTime={blog.date}
          jsonLd={buildBlogPostingJsonLd(blog, content, pageUrl)}
        />
      )}
      <div className="modern-blog-wrapper" dir="rtl" lang="fa" id="article-top">
        <div className="content-wrapper">
          <article className="article-container">
            <header className="article-header">
              <Link to="/blog/index" className="article-back-link"><FaArrowRight aria-hidden="true" /> بازگشت به مقالات</Link>
              <h1 className="article-title iran-sans">{blog?.title}</h1>
            {/* Meta Info */}
            <div className="meta-info">
              <div className="meta-item">
                <div className="author-avatar">
                  <FaUser size={16} />
                </div>
                <span className="iran-sans author-name">
                  {blog?.authorName || "دکتر "}
                </span>
              </div>
              {blog?.dateDisplay && (
                <div className="meta-item">
                  <FaCalendarAlt size={14} />
                  <time className="iran-sans" dateTime={blog.date}>{blog.dateDisplay}</time>
                </div>
              )}
              {blog?.viewCount != null && (
                <div className="meta-item">
                  <FaEye size={14} />
                  <span className="iran-sans">{blog.viewCount.toLocaleString("fa-IR")} بازدید</span>
                </div>
              )}
              <div className="meta-item article-reading-time">
                <FaRegClock size={14} aria-hidden="true" />
                <span>حدود {readingMinutes.toLocaleString("fa-IR")} دقیقه مطالعه</span>
              </div>
            </div>
            </header>

            <div className={`article-reading-layout ${article.headings.length > 1 ? "has-toc" : ""}`}>
              {article.headings.length > 1 && (
                <aside className="article-sidebar">
                  <nav className="article-toc" aria-label="فهرست مطالب مقاله">
                    <h2 className="article-toc-title">در این مقاله می‌خوانید</h2>
                    <button type="button" className="article-toc-toggle" aria-expanded={contentsOpen} aria-controls="article-contents" onClick={() => setContentsOpen((open) => !open)}>
                      فهرست مطالب <FaChevronDown aria-hidden="true" />
                    </button>
                    <ul id="article-contents" className={contentsOpen ? "is-open" : undefined}>
                      {article.headings.map((heading) => (
                        <li key={heading.id} className={heading.level > 2 ? "article-toc-subsection" : undefined}>
                          <a href={`#${heading.id}`} onClick={() => setContentsOpen(false)}>{heading.text}</a>
                        </li>
                      ))}
                    </ul>
                    <a className="article-back-top" href="#article-top">بازگشت به ابتدای مقاله</a>
                  </nav>
                </aside>
              )}
              <div className="article-prose-column">
                {blog?.imageUrl && blog.imageUrl !== failedImage && (
                  <figure className="hero-image-container">
                    <img src={blog.imageUrl} alt={blog.title} className="hero-image" decoding="async" onError={() => setFailedImage(blog.imageUrl)} />
                  </figure>
                )}
                <div className="article-content iran-sans" dangerouslySetInnerHTML={{ __html: article.html }} />

            {/* Admin Actions */}
            {CheckRole(["Admin", "Doctor", "Secretary"]) && (
              <div className="action-buttons">
                <button
                  className="btn-action btn-edit iran-sans"
                  onClick={() => navigate(`/blog/edit/${blog.id}`)}
                >
                  <FaEdit /> <span>ویرایش مقاله</span>
                </button>
                <button
                  className="btn-action btn-delete iran-sans"
                  onClick={() => setOpenDialog(true)}
                >
                  <FaTrash /> <span>حذف مقاله</span>
                </button>
              </div>
            )}
              </div>
            </div>
          </article>

          {/* Comments Section */}
          <div className="comments-section">
            <div className="comments-header">
              <div className="comments-icon-wrapper">
                <img
                  src={AddCommentImg}
                  alt="نظرات"
                  className="comments-icon"
                />
              </div>
              <h2 className="comments-count iran-sans">
                {totalComments} دیدگاه ثبت شده، نظر شما چیست؟
              </h2>
            </div>

            {/* Comment Input */}
            {isLoggedIn() ? (
              <div className="add-comment-form">
                <textarea
                  className="comment-textarea iran-sans"
                  rows="5"
                  placeholder="نظر خود را اینجا بنویسید..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <button
                  className="submit-comment-btn iran-sans"
                  disabled={!commentText.trim()}
                  onClick={onCommentSubmit}
                >
                  ثبت نظر
                </button>
              </div>
            ) : (
              <div className="login-prompt">
                <div className="login-prompt-content iran-sans">
                  <p className="login-prompt-title">
                    نظر شما درباره این مطلب چیست؟
                  </p>
                  <p className="login-prompt-subtitle">
                    برای ثبت نظر وارد شوید
                  </p>
                  <button
                    className="login-btn iran-sans"
                    onClick={() =>
                      navigate("/login", {
                        state: { from: blogDetailPath(blog) },
                      })
                    }
                  >
                    ورود / ثبت‌نام
                  </button>
                </div>
              </div>
            )}

            {/* Comments List */}
            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments iran-sans">هنوز نظری ثبت نشده است.</p>
              ) : (
                comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    userId={userId}
                    onDelete={handleDeleteComment}
                    onReply={handleReplyComment}
                  />
                ))
              )}
            </div>
          </div>
        </div>
        <InfoFooter />
      </div>

      {/* Modals & Notifications */}
      <ConfirmModal
        handleCloseDialog={() => setOpenDialog(false)}
        handleConfirm={handleDeleteBlog}
        openDialog={openDialog}
        text={["آیا از حذف این مقاله اطمینان دارید؟"]}
        confirmText={"بله"}
        cancleText={"خیر"}
      />

      <Notification
        openSnackbar={notification.open}
        snackbarMessage={notification.message}
        snackbarSeverity={notification.severity}
        setOpenSnackbar={(isOpen) =>
          setNotification((prev) => ({ ...prev, open: isOpen }))
        }
      />
    </>
  );
};

export default BlogDetail;
